<template>
  <div class="etm-shell">

    <!-- ── Toolbar ─────────────────────────────────────────────── -->
    <div class="etm-toolbar">
      <!-- Dropdown selector -->
      <div class="etm-selector-wrap">
        <div class="input-group input-group-sm">
          <div class="input-group-prepend">
            <span class="input-group-text"><i class="fas fa-file-code"></i></span>
          </div>
          <select class="form-control form-control-sm etm-select"
                  v-model="selectedFile"
                  @change="onSelectChange">
            <option value="">— select a template —</option>
            <option v-for="f in files" :key="f" :value="f">{{ f }}</option>
          </select>
          <div class="input-group-append" v-if="selectedFile">
            <button class="btn btn-outline-danger btn-sm" title="Delete"
                    @click="confirmDelete(selectedFile)">
              <i class="fas fa-trash-can"></i>
            </button>
          </div>
        </div>
        <div v-if="loadingList" class="ml-2 text-muted small d-flex align-items-center">
          <span class="spinner-border spinner-border-sm mr-1"></span>Loading…
        </div>
      </div>

      <!-- File name + rename -->
      <div class="etm-filename-wrap" v-if="editor.open">
        <div class="input-group input-group-sm">
          <input v-if="editor.renaming"
                 class="form-control form-control-sm"
                 v-model="editor.pendingName"
                 @keyup.enter="applyRename"
                 @keyup.escape="editor.renaming = false"
                 ref="renameInput"
                 placeholder="file.html" />
          <span v-else class="form-control form-control-sm bg-light etm-fname">{{ editor.fileName }}</span>
          <div class="input-group-append">
            <button v-if="!editor.renaming" class="btn btn-outline-secondary btn-sm" title="Rename"
                    @click="startRename"><i class="fas fa-pen-to-square"></i></button>
            <button v-if="editor.renaming" class="btn btn-outline-success btn-sm" @click="applyRename">
              <i class="fas fa-check"></i></button>
            <button v-if="editor.renaming" class="btn btn-outline-secondary btn-sm"
                    @click="editor.renaming = false"><i class="fas fa-times"></i></button>
          </div>
        </div>
        <small v-if="editor.isNew" class="text-muted ml-1">New — saved on first Save</small>
      </div>

      <!-- Viewport controls (shown when preview pane is visible) -->
      <div class="etm-viewport-btns" v-if="editor.open">
        <button v-for="vp in viewports" :key="vp.key"
                class="btn btn-sm"
                :class="viewport === vp.key ? 'btn-primary' : 'btn-outline-secondary'"
                :title="vp.label"
                @click="viewport = vp.key">
          <i :class="vp.icon"></i>
        </button>
      </div>

      <!-- Dark / Light mode toggle -->
      <div class="etm-mode-toggle" v-if="editor.open">
        <button class="btn btn-sm"
                :class="darkMode ? 'btn-dark' : 'btn-outline-secondary'"
                :title="darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'"
                @click="darkMode = !darkMode">
          <i :class="darkMode ? 'fas fa-sun' : 'fas fa-moon'"></i>
          <span class="ml-1">{{ darkMode ? 'Light' : 'Dark' }}</span>
        </button>
      </div>

      <!-- Actions -->
      <div class="etm-actions ml-auto d-flex align-items-center gap-2">
        <span v-if="editor.dirty" class="badge badge-warning mr-2">Unsaved</span>
        <span v-else-if="editor.savedAt" class="text-success small mr-2">
          <i class="fas fa-check"></i> {{ editor.savedAt }}
        </span>
        <button class="btn btn-sm btn-outline-secondary mr-1"
                @click="newTemplate">
          <i class="fas fa-plus"></i> New
        </button>
        <button class="btn btn-sm btn-outline-secondary mr-1"
                :disabled="editor.isNew || editor.loading || !editor.open"
                @click="reloadFile" title="Discard changes">
          <i class="fas fa-rotate-left"></i>
        </button>
        <button class="btn btn-sm btn-spnm"
                :disabled="editor.saving || !editor.open"
                @click="saveTemplate">
          <span v-if="editor.saving"><span class="spinner-border spinner-border-sm"></span></span>
          <span v-else><i class="fas fa-save"></i> Save</span>
        </button>
      </div>
    </div>

    <!-- ── Empty state ─────────────────────────────────────────── -->
    <div v-if="!editor.open" class="etm-empty">
      <i class="fas fa-file-code etm-empty-icon"></i>
      <p class="mt-3 text-muted">Select a template above or click <strong>New</strong> to create one.</p>
    </div>

    <!-- ── Split pane: Editor | Preview ──────────────────────── -->
    <div v-else class="etm-split">

      <!-- Left: HTML editor -->
      <div class="etm-editor-pane">
        <div class="etm-pane-header" :class="{ 'etm-pane-header--dark': darkMode }">
          <i class="fas fa-code mr-1"></i>HTML Editor
        </div>
        <div v-if="editor.loading" class="etm-loading" :class="{ 'etm-loading--dark': darkMode }">
          <span class="spinner-border spinner-border-sm text-primary"></span> Loading…
        </div>
        <textarea v-else
          class="etm-textarea custom-scroller"
          :class="{ 'etm-textarea--dark': darkMode }"
          v-model="editor.content"
          spellcheck="false"></textarea>
      </div>

      <!-- Resize handle -->
      <div class="etm-divider" @mousedown="startResize"></div>

      <!-- Right: Preview -->
      <div class="etm-preview-pane" :style="{ width: previewWidth + 'px' }">
        <div class="etm-pane-header d-flex align-items-center" :class="{ 'etm-pane-header--dark': darkMode }">
          <i class="fas fa-eye mr-1"></i>Preview
          <span class="ml-2 text-muted small">
            {{ currentViewport.label }} — {{ currentViewport.w }}
          </span>
        </div>
        <div class="etm-preview-stage" :class="{ 'etm-preview-stage--dark': darkMode }">
          <div class="etm-preview-frame-wrap"
               :style="previewFrameStyle">
            <iframe
              ref="previewFrame"
              :srcdoc="darkMode ? darkWrappedContent : editor.content"
              class="etm-iframe"
              sandbox="allow-same-origin"
            ></iframe>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Delete modal ────────────────────────────────────────── -->
    <div class="modal fade" id="deleteTemplateModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-sm">
        <div class="modal-content">
          <div class="modal-header">
            <h6 class="modal-title">Delete Template</h6>
            <button type="button" class="close" data-dismiss="modal"><span>&times;</span></button>
          </div>
          <div class="modal-body">
            Delete <strong>{{ deleteTarget }}</strong>? This cannot be undone.
          </div>
          <div class="modal-footer">
            <button class="btn btn-sm btn-secondary" data-dismiss="modal">Cancel</button>
            <button class="btn btn-sm btn-danger" :disabled="deleting" @click="doDelete">
              <span v-if="deleting"><span class="spinner-border spinner-border-sm"></span></span>
              <span v-else>Delete</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
export default {
  name: "EmailTemplateManager",
  data() {
    return {
      files: [],
      loadingList: false,
      selectedFile: "",
      deleteTarget: null,
      deleting: false,
      // viewport switcher
      viewport: "desktop",
      viewports: [
        { key: "desktop", label: "Desktop",  icon: "fas fa-desktop",  w: "100%",  maxW: null  },
        { key: "tablet",  label: "Tablet",   icon: "fas fa-tablet",   w: "768px", maxW: 768   },
        { key: "mobile",  label: "Mobile",   icon: "fas fa-mobile",   w: "375px", maxW: 375   },
      ],
      // dark mode
      darkMode: false,
      // draggable divider
      previewWidth: 480,
      resizing: false,
      resizeStartX: 0,
      resizeStartW: 0,
      editor: {
        open: false,
        isNew: false,
        fileName: "",
        pendingName: "",
        renaming: false,
        content: "",
        loading: false,
        saving: false,
        dirty: false,
        savedAt: null,
      },
    };
  },
  computed: {
    currentViewport() {
      return this.viewports.find((v) => v.key === this.viewport);
    },
    darkWrappedContent() {
      // Inject a dark background + light text override into the preview iframe
      const darkStyle = `<style>
        html,body{background:#1e1e1e!important;color:#d4d4d4!important;}
        *{color:inherit;border-color:#444!important;}
        a{color:#6db3f2!important;}
        img{opacity:.9;}
      </style>`;
      const content = this.editor.content || "";
      if (content.includes("</head>")) {
        return content.replace("</head>", darkStyle + "</head>");
      }
      return darkStyle + content;
    },
    previewFrameStyle() {
      const vp = this.currentViewport;
      if (vp.key === "desktop") return { width: "100%", height: "100%" };
      return {
        width: vp.w,
        maxWidth: "100%",
        height: "100%",
        margin: "0 auto",
        boxShadow: "0 0 0 1px #ccc",
        borderRadius: "4px",
        overflow: "hidden",
      };
    },
  },
  watch: {
    "editor.content"() {
      if (this.editor.open && !this.editor.loading) this.editor.dirty = true;
    },
  },
  created() {
    this.loadFileList();
  },
  mounted() {
    document.addEventListener("mousemove", this.onResize);
    document.addEventListener("mouseup", this.stopResize);
  },
  beforeDestroy() {
    document.removeEventListener("mousemove", this.onResize);
    document.removeEventListener("mouseup", this.stopResize);
  },
  methods: {
    loadFileList() {
      this.loadingList = true;
      axios.get("/api/Event/GetTemplateList")
        .then((r) => { this.files = r.data; })
        .catch(() => { alert("Failed to load template list."); })
        .finally(() => { this.loadingList = false; });
    },

    onSelectChange() {
      if (this.selectedFile) this.selectFile(this.selectedFile);
    },

    selectFile(fileName) {
      if (this.editor.dirty && !confirm("You have unsaved changes. Discard?")) {
        this.selectedFile = this.editor.fileName; // revert dropdown
        return;
      }
      this.selectedFile = fileName;
      this.editor = {
        open: true, isNew: false,
        fileName, pendingName: fileName,
        renaming: false, content: "",
        loading: true, saving: false,
        dirty: false, savedAt: null,
      };
      axios.get("/api/Event/GetTemplate", { params: { fileName } })
        .then((r) => { this.editor.content = r.data.content; this.editor.dirty = false; })
        .catch(() => { alert("Failed to load template."); })
        .finally(() => { this.editor.loading = false; });
    },

    reloadFile() {
      if (!this.editor.isNew) this.selectFile(this.editor.fileName);
    },

    newTemplate() {
      if (this.editor.dirty && !confirm("Discard unsaved changes?")) return;
      this.selectedFile = "";
      this.editor = {
        open: true, isNew: true,
        fileName: "NewTemplate.html", pendingName: "NewTemplate.html",
        renaming: false,
        content: '<!DOCTYPE html>\n<html>\n<head><meta charset="utf-8" /><title></title></head>\n<body>\n\n</body>\n</html>',
        loading: false, saving: false, dirty: true, savedAt: null,
      };
    },

    startRename() {
      this.editor.pendingName = this.editor.fileName;
      this.editor.renaming = true;
      this.$nextTick(() => { if (this.$refs.renameInput) this.$refs.renameInput.focus(); });
    },

    applyRename() {
      const newName = (this.editor.pendingName || "").trim();
      if (!newName) return;
      const normalized = newName.endsWith(".html") ? newName : newName + ".html";
      if (this.editor.isNew) { this.editor.fileName = normalized; this.editor.renaming = false; return; }
      axios.post("/api/Event/RenameTemplate", { oldFileName: this.editor.fileName, newFileName: normalized })
        .then((r) => {
          const idx = this.files.indexOf(this.editor.fileName);
          if (idx !== -1) this.files.splice(idx, 1, r.data.newFileName);
          this.selectedFile = r.data.newFileName;
          this.editor.fileName = r.data.newFileName;
          this.editor.renaming = false;
        })
        .catch((err) => { alert(err.response?.data || "Rename failed."); });
    },

    saveTemplate() {
      if (!this.editor.fileName) { alert("Please set a file name."); return; }
      this.editor.saving = true;
      axios.post("/api/Event/SaveTemplate", { fileName: this.editor.fileName, content: this.editor.content })
        .then((r) => {
          if (!this.files.includes(r.data.fileName)) { this.files.push(r.data.fileName); this.files.sort(); }
          this.selectedFile = r.data.fileName;
          this.editor.fileName = r.data.fileName;
          this.editor.isNew = false;
          this.editor.dirty = false;
          this.editor.savedAt = new Date().toLocaleTimeString();
        })
        .catch(() => { alert("Failed to save template."); })
        .finally(() => { this.editor.saving = false; });
    },

    confirmDelete(fileName) {
      this.deleteTarget = fileName;
      $("#deleteTemplateModal").modal("show");
    },

    doDelete() {
      this.deleting = true;
      axios.delete("/api/Event/DeleteTemplate", { params: { fileName: this.deleteTarget } })
        .then(() => {
          this.files = this.files.filter((f) => f !== this.deleteTarget);
          if (this.selectedFile === this.deleteTarget) {
            this.selectedFile = "";
            this.editor.open = false;
          }
          this.deleteTarget = null;
          $("#deleteTemplateModal").modal("hide");
        })
        .catch((err) => { alert(err.response?.data || "Delete failed."); })
        .finally(() => { this.deleting = false; });
    },

    // ── Drag-resize divider ────────────────────────────────────
    startResize(e) {
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartW = this.previewWidth;
      e.preventDefault();
    },
    onResize(e) {
      if (!this.resizing) return;
      const delta = this.resizeStartX - e.clientX; // dragging left = bigger preview
      this.previewWidth = Math.max(280, Math.min(900, this.resizeStartW + delta));
    },
    stopResize() { this.resizing = false; },
  },
};
</script>

<style scoped>
/* ── Shell fills the parent content-card ─────────────────────── */
.etm-shell {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

/* ── Toolbar ─────────────────────────────────────────────────── */
.etm-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4e8f0;
  flex-shrink: 0;
}
.etm-selector-wrap { display: flex; align-items: center; min-width: 240px; max-width: 320px; flex: 1; }
.etm-select { max-width: 260px; }
.etm-filename-wrap { min-width: 200px; max-width: 280px; flex: 1; }
.etm-fname { font-family: monospace; font-size: 12.5px; }
.etm-viewport-btns { display: flex; gap: 4px; }
.etm-mode-toggle { display: flex; align-items: center; }
.etm-actions { display: flex; align-items: center; flex-shrink: 0; }
.gap-2 { gap: 8px; }

/* ── Empty ───────────────────────────────────────────────────── */
.etm-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #a0aec0;
}
.etm-empty-icon { font-size: 56px; opacity: 0.3; }

/* ── Split pane ──────────────────────────────────────────────── */
.etm-split {
  display: flex;
  flex: 1;
  min-height: 0;
  margin-top: 10px;
  gap: 0;
}

/* Editor pane */
.etm-editor-pane {
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.etm-pane-header {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #718096;
  padding: 4px 6px;
  background: #f7f8fa;
  border: 1px solid #e4e8f0;
  border-bottom: none;
  border-radius: 6px 6px 0 0;
  flex-shrink: 0;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
}
.etm-pane-header--dark {
  background: #252526;
  color: #9da5b4;
  border-color: #3c3c3c;
}
.etm-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e4e8f0;
  color: #718096;
}
.etm-loading--dark {
  background: #1e1e1e;
  border-color: #3c3c3c;
  color: #888;
}
.etm-textarea {
  flex: 1;
  resize: none;
  font-family: 'Courier New', Courier, monospace;
  font-size: 12.5px;
  border-radius: 0 0 6px 6px;
  border: 1px solid #e4e8f0;
  padding: 10px;
  min-height: 0;
  overflow-y: auto;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
}
.etm-textarea--dark {
  background: #1e1e1e;
  color: #d4d4d4;
  border-color: #3c3c3c;
  caret-color: #d4d4d4;
}

/* Draggable divider */
.etm-divider {
  width: 6px;
  cursor: col-resize;
  background: #e4e8f0;
  flex-shrink: 0;
  border-radius: 3px;
  margin: 0 4px;
  transition: background 0.15s;
}
.etm-divider:hover, .etm-divider:active { background: #2d4fa1; }

/* Preview pane */
.etm-preview-pane {
  display: flex;
  flex-direction: column;
  min-width: 280px;
  min-height: 0;
  flex-shrink: 0;
}
.etm-preview-stage {
  flex: 1;
  border: 1px solid #e4e8f0;
  border-radius: 0 0 6px 6px;
  overflow: hidden;
  background: #f0f2f8;
  display: flex;
  flex-direction: column;
  min-height: 0;
  transition: background 0.2s, border-color 0.2s;
}
.etm-preview-stage--dark {
  background: #1e1e1e;
  border-color: #3c3c3c;
}
.etm-preview-frame-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  transition: width 0.2s;
}
.etm-iframe {
  flex: 1;
  border: none;
  width: 100%;
  height: 100%;
  display: block;
}
</style>
